import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  Search, 
  Filter, 
  Mail, 
  Phone,
  Calendar,
  Award,
  CheckCircle,
  Clock,
  MoreVertical,
  Eye,
  MessageSquare,
  BookOpen,
  TrendingUp,
  Loader2,
  X,
  ChevronDown,
  Tag,
  Briefcase,
  Building,
  MapPin,
  User,
  Info
} from 'lucide-react';
import { trainingsService, Training } from '../../services/trainings.service';
import paymentReceiptsService, { Subscription } from '../../services/payment-receipts.service';
import { useTranslation } from 'react-i18next';

interface Participant {
  id: number;
  name: string;
  email: string;
  phone: string;
  training: string;
  trainingId: string;
  sessionId?: number;
  sessionDate?: string;
  joinDate: string;
  lastActivity: string;
  certificate: string | null;
  image: string;
  subscriptionId?: string;
}

interface Session {
  id: number;
  trainingId: number;
  trainingTitle: string;
  sessionDate: string;
  participantCount: number;
  maxParticipants: number;
  availableSpots: number;
}

const ParticipantsPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedTraining, setSelectedTraining] = useState('all');
  const [selectedSession, setSelectedSession] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({
    total: 0,
    inProgress: 0,
    completed: 0,
    certified: 0
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);
  const [participantDetails, setParticipantDetails] = useState<any | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Helper function to validate API response structure
  const validateApiResponse = (response: any) => {
    if (!response || typeof response !== 'object') {
      return false;
    }
    
    // Check if required properties exist and are of correct type
    const hasParticipants = Array.isArray(response.participants);
    const hasStats = response.stats && typeof response.stats === 'object';
    const hasPagination = response.pagination && typeof response.pagination === 'object';
    
    return hasParticipants && hasStats && hasPagination;
  };

  

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Fetch sessions when training changes
    if (selectedTraining !== 'all') {
      fetchSessions(selectedTraining);
    } else {
      fetchSessions();
    }
  }, [selectedTraining]);

  useEffect(() => {
    // Debounce search to avoid too many API calls
    const timeoutId = setTimeout(() => {
      fetchParticipants();
    }, searchTerm ? 500 : 0); // Only debounce when searching
    
    return () => clearTimeout(timeoutId);
  }, [selectedSession, selectedTraining, selectedStatus, searchTerm]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      
      // Fetch trainings, subscriptions, and participants
      const [trainingsData, subscriptionsData] = await Promise.all([
        trainingsService.getAllTrainings().catch((err) => {
          return [];
        }),
        paymentReceiptsService.getMySubscriptions().catch((err) => {
          return [];
        })
      ]);
      
      setTrainings(trainingsData || []);
      setSubscriptions(subscriptionsData || []);
      
      // Fetch participants separately to handle its specific error handling
      await fetchParticipants();
      
    } catch (err) {
      setError(t('trainingCenter.participants.error.loadData'));
    } finally {
      setLoading(false);
    }
  };

  const fetchParticipants = async () => {
    try {
      const filters = {
        trainingId: selectedTraining !== 'all' ? selectedTraining : undefined,
        sessionId: selectedSession !== 'all' ? selectedSession : undefined,
        status: selectedStatus !== 'all' ? selectedStatus : undefined,
        search: searchTerm || undefined,
        page: pagination.page,
        limit: pagination.limit
      };

      const response = await trainingsService.getParticipants(filters);
      
      // Validate API response structure
      if (!validateApiResponse(response)) {
        throw new Error('Invalid response structure from server');
      }
      
      // Defensive programming: ensure response has expected structure
      setParticipants(response.participants || []);
      setStats(response.stats || {
        total: 0,
        inProgress: 0,
        completed: 0,
        certified: 0
      });
      setPagination(response.pagination || {
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 0
      });
      
    } catch (err) {
      setError(t('trainingCenter.participants.error.loadParticipants'));
      // Reset to safe defaults on error
      setParticipants([]);
      setStats({
        total: 0,
        inProgress: 0,
        completed: 0,
        certified: 0
      });
      setPagination({
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 0
      });
    }
  };

  const fetchSessions = async (trainingId?: string) => {
    try {
      const sessionsData = await trainingsService.getSessions(trainingId);
      setSessions(sessionsData);
    } catch (err) {
    }
  };

  // Real-time filtering is now handled by the API, so we don't need client-side filtering
  const filteredParticipants = participants;


  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedStatus('all');
    setSelectedTraining('all');
    setSelectedSession('all');
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (selectedTraining !== 'all') count++;
    if (selectedSession !== 'all') count++;
    return count;
  };

  // Handle filter changes
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleStatusChange = (status: string) => {
    setSelectedStatus(status);
  };

  const handleTrainingChange = (trainingId: string) => {
    setSelectedTraining(trainingId);
    setSelectedSession('all'); // Reset session when training changes
  };

  const handleSessionChange = (sessionId: string) => {
    setSelectedSession(sessionId);
  };

  const handleViewParticipant = async (participant: Participant) => {
    try {
      setSelectedParticipant(participant);
      setLoadingDetails(true);
      setIsModalOpen(true);
      
      const details = await trainingsService.getParticipantDetails(participant.id);
      setParticipantDetails(details);
    } catch (error) {
      setParticipantDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedParticipant(null);
    setParticipantDetails(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <button 
          onClick={fetchData}
          className="bg-brand-primary text-white px-4 py-2 rounded-lg hover:bg-brand-primary/90"
        >
          {t('trainingCenter.participants.error.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-surface">
      <div className="space-y-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-brand-primary to-brand-secondary rounded-2xl p-8 text-white">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-3">
                {t('trainingCenter.participants.title')}
              </h1>
              <p className="text-white/90 text-lg">
                {t('trainingCenter.participants.subtitle')}
              </p>
            </div>
            <div className="mt-6 lg:mt-0 flex items-center gap-4">
              <div className="text-right">
                <div className="text-2xl font-bold">{stats?.total || 0}</div>
                <div className="text-white/80 text-sm">{t('trainingCenter.participants.totalParticipants')}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-4">
          <div className="flex flex-col gap-4">
            {/* Search Bar and Quick Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t('trainingCenter.participants.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all duration-200 text-sm"
                  />
                </div>
              </div>

              {/* Quick Filter Buttons - Responsive */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleStatusChange(selectedStatus === 'En cours' ? 'all' : 'En cours')}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 ${
                    selectedStatus === 'En cours' 
                      ? 'bg-brand-primary text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t('trainingCenter.participants.status.inProgress')}
                </button>
                <button
                  onClick={() => handleStatusChange(selectedStatus === 'Terminé' ? 'all' : 'Terminé')}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 ${
                    selectedStatus === 'Terminé' 
                      ? 'bg-brand-secondary text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t('trainingCenter.participants.status.completed')}
                </button>
                <button
                  onClick={() => handleStatusChange(selectedStatus === 'En attente' ? 'all' : 'En attente')}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 ${
                    selectedStatus === 'En attente' 
                      ? 'bg-brand-mint text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t('trainingCenter.participants.status.pending')}
                </button>
              </div>

              {/* Advanced Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center justify-center gap-2 px-3 py-2.5 border border-gray-200 rounded-lg hover:border-brand-primary hover:bg-brand-primary/5 transition-all duration-200 min-w-[120px]"
              >
                <Filter className="h-4 w-4" />
                <span className="text-sm font-medium hidden sm:inline">{t('trainingCenter.participants.filters.more')}</span>
                <span className="text-sm font-medium sm:hidden">{t('trainingCenter.participants.filters.title')}</span>
                {getActiveFiltersCount() > 0 && (
                  <span className="bg-brand-primary text-white text-xs rounded-full px-1.5 py-0.5 min-w-[16px] text-center">
                    {getActiveFiltersCount()}
                  </span>
                )}
                <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${showFilters ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="border-t border-gray-100 pt-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Training Filter */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {t('trainingCenter.participants.filters.training')}
                    </label>
                    <select
                      value={selectedTraining}
                      onChange={(e) => handleTrainingChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all duration-200 text-sm"
                    >
                      <option value="all">{t('trainingCenter.participants.filters.allTrainings')}</option>
                      {trainings.map((training) => (
                        <option key={training.id} value={training.id}>
                          {training.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Session Filter */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {t('trainingCenter.participants.filters.session')}
                    </label>
                    <select
                      value={selectedSession}
                      onChange={(e) => handleSessionChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all duration-200 text-sm"
                      disabled={selectedTraining === 'all'}
                    >
                      <option value="all">
                        {selectedTraining === 'all' ? t('trainingCenter.participants.filters.selectTrainingFirst') : t('trainingCenter.participants.filters.allSessions')}
                      </option>
                      {sessions.map((session) => (
                        <option key={session.id} value={session.id}>
                          {new Date(session.sessionDate).toLocaleDateString('fr-FR')} - {session.trainingTitle}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Clear Filters */}
                {getActiveFiltersCount() > 0 && (
                  <div className="mt-3 flex justify-center sm:justify-end">
                    <button
                      onClick={clearAllFilters}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-600 hover:text-brand-primary transition-colors duration-200"
                    >
                      <X className="h-3 w-3" />
                      {t('trainingCenter.participants.filters.clearAll')}
                    </button>
                  </div>
                )}
              </motion.div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-md border border-gray-100 p-4 hover:shadow-lg transition-all duration-300 group"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1">{t('trainingCenter.participants.stats.total')}</p>
                <p className="text-xl font-bold text-gray-900">{stats?.total || 0}</p>
              </div>
              <div className="w-10 h-10 bg-gradient-to-br from-brand-primary to-brand-primary/80 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Users className="h-5 w-5 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-md border border-gray-100 p-4 hover:shadow-lg transition-all duration-300 group"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1">{t('trainingCenter.participants.stats.inProgress')}</p>
                <p className="text-xl font-bold text-gray-900">{stats?.inProgress || 0}</p>
              </div>
              <div className="w-10 h-10 bg-gradient-to-br from-brand-secondary to-brand-secondary/80 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Clock className="h-5 w-5 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-md border border-gray-100 p-4 hover:shadow-lg transition-all duration-300 group"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1">{t('trainingCenter.participants.stats.completed')}</p>
                <p className="text-xl font-bold text-gray-900">{stats?.completed || 0}</p>
              </div>
              <div className="w-10 h-10 bg-gradient-to-br from-brand-mint to-brand-mint/80 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <CheckCircle className="h-5 w-5 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-xl shadow-md border border-gray-100 p-4 hover:shadow-lg transition-all duration-300 group"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1">{t('trainingCenter.participants.stats.certified')}</p>
                <p className="text-xl font-bold text-gray-900">{stats?.certified || 0}</p>
              </div>
              <div className="w-10 h-10 bg-gradient-to-br from-brand-accent to-brand-accent/80 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Award className="h-5 w-5 text-white" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Sessions and Participants Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sessions List - Show when a formation is selected */}
          {selectedTraining !== 'all' && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:col-span-1 order-2 lg:order-1"
            >
              <div className="bg-white rounded-xl shadow-md border border-gray-100">
                <div className="p-4 border-b border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-brand-primary" />
                    {t('trainingCenter.participants.sessions.title')}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {sessions.length} {sessions.length !== 1 ? t('trainingCenter.participants.sessions.availablePlural') : t('trainingCenter.participants.sessions.availableSingular')}
                  </p>
                </div>
                <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                  {sessions.length > 0 ? (
                    sessions.map((session, index) => (
                      <motion.div
                        key={session.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className={`p-3 rounded-lg border cursor-pointer transition-all duration-200 ${
                          selectedSession === session.id.toString()
                            ? 'border-brand-primary bg-brand-primary/5'
                            : 'border-gray-200 hover:border-brand-primary/50 hover:bg-gray-50'
                        }`}
                        onClick={() => handleSessionChange(session.id.toString())}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 text-sm">
                              {new Date(session.sessionDate).toLocaleDateString('fr-FR', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                            <p className="text-xs text-gray-600 mt-1">
                              {session.participantCount} {session.participantCount !== 1 ? t('trainingCenter.participants.sessions.participantsPlural') : t('trainingCenter.participants.sessions.participantsSingular')}
                            </p>
                          </div>
                          <div className="flex justify-end sm:block">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              session.availableSpots > 0 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {session.availableSpots > 0 ? `${session.availableSpots} ${t('trainingCenter.participants.sessions.places')}` : t('trainingCenter.participants.sessions.full')}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <Calendar className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">{t('trainingCenter.participants.sessions.noSessions')}</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Participants Table */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className={selectedTraining !== 'all' ? 'lg:col-span-2 order-1 lg:order-2' : 'lg:col-span-3'}
          >
            <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
              <div className="p-4 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Users className="h-5 w-5 text-brand-primary" />
                  {t('trainingCenter.participants.title')}
                  {selectedTraining !== 'all' && (
                    <span className="text-sm font-normal text-gray-600">
                      - {filteredParticipants.length} {filteredParticipants.length !== 1 ? t('trainingCenter.participants.participantsPlural') : t('trainingCenter.participants.participantsSingular')}
                  </span>
                  )}
                </h3>
              </div>

              {filteredParticipants.length > 0 ? (
                <>
                  {/* Desktop Table View */}
                  <div className="hidden lg:block overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('trainingCenter.participants.table.participant')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('trainingCenter.participants.table.training')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('trainingCenter.participants.table.session')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('trainingCenter.participants.table.phone')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('trainingCenter.participants.table.subscription')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('trainingCenter.participants.table.actions')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredParticipants.map((participant, index) => (
                          <motion.tr
                            key={participant.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="hover:bg-gray-50 transition-colors duration-200"
                          >
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10">
                                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-brand-primary to-brand-secondary flex items-center justify-center text-white font-semibold text-sm">
                                    {participant.name.charAt(0).toUpperCase()}
                                  </div>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">
                                    {participant.name}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {participant.subscriptionId ? `${t('trainingCenter.participants.table.subscriptionId')}${participant.subscriptionId}` : t('trainingCenter.participants.table.participant')}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{participant.training}</div>
                              <div className="text-sm text-gray-500">
                                {participant.joinDate ? `${t('trainingCenter.participants.table.enrolledOn')} ${new Date(participant.joinDate).toLocaleDateString('fr-FR')}` : t('trainingCenter.participants.table.training')}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {participant.sessionDate ? new Date(participant.sessionDate).toLocaleDateString('fr-FR', {
                                  weekday: 'short',
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                }) : t('trainingCenter.participants.table.notAvailable')}
                              </div>
                              <div className="text-sm text-gray-500">
                                {participant.sessionId ? `${t('trainingCenter.participants.table.sessionId')}${participant.sessionId}` : t('trainingCenter.participants.table.session')}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{participant.phone || t('trainingCenter.participants.table.notAvailable')}</div>
                              <div className="text-sm text-gray-500">{participant.email}</div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(participant.joinDate).toLocaleDateString('fr-FR')}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex items-center space-x-2">
                                <button 
                                  onClick={() => handleViewParticipant(participant)}
                                  className="text-brand-primary hover:text-brand-primary/80 transition-colors"
                                  title={t('trainingCenter.participants.table.viewDetails')}
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                                <button className="text-brand-secondary hover:text-brand-secondary/80 transition-colors">
                                  <MessageSquare className="h-4 w-4" />
                                </button>
                                <button className="text-gray-400 hover:text-gray-600 transition-colors">
                                  <MoreVertical className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="lg:hidden space-y-4">
                    {filteredParticipants.map((participant, index) => (
                      <motion.div
                        key={participant.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow duration-200"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-brand-primary to-brand-secondary flex items-center justify-center text-white font-semibold text-lg">
                              {participant.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-semibold text-gray-900 truncate">
                                {participant.name}
                              </h3>
                              <p className="text-xs text-gray-500">
                                {participant.subscriptionId ? `${t('trainingCenter.participants.table.subscriptionId')}${participant.subscriptionId}` : t('trainingCenter.participants.table.participant')}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2 mb-4">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">{t('trainingCenter.participants.mobile.training')}:</span>
                            <span className="font-medium text-gray-900 truncate ml-2">{participant.training}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">{t('trainingCenter.participants.mobile.session')}:</span>
                            <span className="font-medium text-gray-900">
                              {participant.sessionDate ? new Date(participant.sessionDate).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              }) : t('trainingCenter.participants.table.notAvailable')}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">{t('trainingCenter.participants.mobile.email')}:</span>
                            <span className="font-medium text-gray-900 truncate ml-2">{participant.email}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">{t('trainingCenter.participants.mobile.phone')}:</span>
                            <span className="font-medium text-gray-900">{participant.phone || t('trainingCenter.participants.table.notAvailable')}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">{t('trainingCenter.participants.mobile.subscription')}:</span>
                            <span className="font-medium text-gray-900">
                              {new Date(participant.joinDate).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center justify-end">
                          <div className="flex items-center space-x-2">
                            <button 
                              onClick={() => handleViewParticipant(participant)}
                              className="p-2 text-brand-primary hover:text-brand-primary/80 transition-colors"
                              title={t('trainingCenter.participants.table.viewDetails')}
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button className="p-2 text-brand-secondary hover:text-brand-secondary/80 transition-colors">
                              <MessageSquare className="h-4 w-4" />
                            </button>
                            <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                              <MoreVertical className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-16">
            <div className="w-24 h-24 bg-gradient-to-br from-brand-primary/10 to-brand-secondary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Users className="h-12 w-12 text-brand-primary" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              {participants.length === 0 ? t('trainingCenter.participants.emptyState.title') : t('trainingCenter.participants.emptyState.subtitle')}
            </h3>
            <p className="text-gray-600 mb-8 text-lg">
              {participants.length === 0 
                ? t('trainingCenter.participants.emptyState.noParticipants')
                : t('trainingCenter.participants.emptyState.tryFilters')
              }
            </p>
                </div>
              )}
              </div>
          </motion.div>
        </div>

      </div>

      {/* Participant Details Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-brand-primary to-brand-secondary text-white p-6 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="h-12 w-12 rounded-full bg-white/20 flex items-center justify-center">
                  <User className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">
                    {selectedParticipant?.name || t('trainingCenter.participants.modal.title')}
                  </h2>
                  <p className="text-white/80 text-sm">
                    {t('trainingCenter.participants.modal.subtitle')}
                  </p>
                </div>
              </div>
              <button
                onClick={closeModal}
                className="text-white hover:text-white/80 transition-colors p-2"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingDetails ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
                </div>
              ) : participantDetails ? (
                <div className="space-y-6">
                  {/* User Information */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                      <User className="h-5 w-5 text-brand-primary" />
                      <span>{t('trainingCenter.participants.modal.userInfo.title')}</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500">{t('trainingCenter.participants.modal.userInfo.firstname')}</label>
                        <p className="text-sm font-medium text-gray-900 mt-1">
                          {participantDetails.user?.firstname || participantDetails.trainee?.firstname || t('trainingCenter.participants.table.notAvailable')}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500">{t('trainingCenter.participants.modal.userInfo.lastname')}</label>
                        <p className="text-sm font-medium text-gray-900 mt-1">
                          {participantDetails.user?.lastname || participantDetails.trainee?.lastname || t('trainingCenter.participants.table.notAvailable')}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 flex items-center space-x-1">
                          <Mail className="h-3 w-3" />
                          <span>{t('trainingCenter.participants.modal.userInfo.email')}</span>
                        </label>
                        <p className="text-sm font-medium text-gray-900 mt-1">
                          {participantDetails.user?.email || participantDetails.trainee?.email || t('trainingCenter.participants.table.notAvailable')}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 flex items-center space-x-1">
                          <Phone className="h-3 w-3" />
                          <span>{t('trainingCenter.participants.modal.userInfo.phone')}</span>
                        </label>
                        <p className="text-sm font-medium text-gray-900 mt-1">
                          {participantDetails.trainee?.phone || selectedParticipant?.phone || t('trainingCenter.participants.table.notAvailable')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Subscription Information */}
                  {participantDetails.subscription && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                        <Briefcase className="h-5 w-5 text-brand-secondary" />
                        <span>{t('trainingCenter.participants.modal.professionalInfo.title')}</span>
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {participantDetails.subscription.current_position && (
                          <div>
                            <label className="text-xs font-medium text-gray-500">{t('trainingCenter.participants.modal.professionalInfo.currentPosition')}</label>
                            <p className="text-sm font-medium text-gray-900 mt-1">
                              {participantDetails.subscription.current_position}
                            </p>
                          </div>
                        )}
                        {participantDetails.subscription.company_name && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 flex items-center space-x-1">
                              <Building className="h-3 w-3" />
                              <span>{t('trainingCenter.participants.modal.professionalInfo.company')}</span>
                            </label>
                            <p className="text-sm font-medium text-gray-900 mt-1">
                              {participantDetails.subscription.company_name}
                            </p>
                          </div>
                        )}
                        {participantDetails.subscription.department && (
                          <div>
                            <label className="text-xs font-medium text-gray-500">{t('trainingCenter.participants.modal.professionalInfo.department')}</label>
                            <p className="text-sm font-medium text-gray-900 mt-1">
                              {participantDetails.subscription.department}
                            </p>
                          </div>
                        )}
                        {participantDetails.subscription.years_of_experience !== null && participantDetails.subscription.years_of_experience !== undefined && (
                          <div>
                            <label className="text-xs font-medium text-gray-500">{t('trainingCenter.participants.modal.professionalInfo.yearsOfExperience')}</label>
                            <p className="text-sm font-medium text-gray-900 mt-1">
                              {participantDetails.subscription.years_of_experience} {participantDetails.subscription.years_of_experience > 1 ? t('trainingCenter.participants.modal.professionalInfo.yearsPlural') : t('trainingCenter.participants.modal.professionalInfo.yearsSingular')}
                            </p>
                          </div>
                        )}
                        {(participantDetails.subscription.has_special_accommodation || participantDetails.subscription.special_accommodation_details) && (
                          <div className="md:col-span-2">
                            <label className="text-xs font-medium text-gray-500 flex items-center space-x-1">
                              <Info className="h-3 w-3" />
                              <span>{t('trainingCenter.participants.modal.professionalInfo.specialArrangement')}</span>
                            </label>
                            <p className="text-sm font-medium text-gray-900 mt-1">
                              {participantDetails.subscription.has_special_accommodation ? t('trainingCenter.participants.modal.professionalInfo.yes') : t('trainingCenter.participants.modal.professionalInfo.no')}
                            </p>
                            {participantDetails.subscription.special_accommodation_details && (
                              <div className="mt-2">
                                <label className="text-xs font-medium text-gray-500 mb-1 block">{t('trainingCenter.participants.modal.professionalInfo.specialArrangementDetails')}</label>
                                <p className="text-sm text-gray-700 bg-blue-50 p-3 rounded border border-blue-200 whitespace-pre-wrap">
                                  {participantDetails.subscription.special_accommodation_details}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Training/Session Information */}
                  {participantDetails.subscription?.session && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                        <Calendar className="h-5 w-5 text-brand-mint" />
                        <span>{t('trainingCenter.participants.modal.trainingSession.title')}</span>
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {participantDetails.subscription.session.training && (
                          <div>
                            <label className="text-xs font-medium text-gray-500">{t('trainingCenter.participants.modal.trainingSession.training')}</label>
                            <p className="text-sm font-medium text-gray-900 mt-1">
                              {participantDetails.subscription.session.training.title}
                            </p>
                          </div>
                        )}
                        {participantDetails.subscription.session.session_date && (
                          <div>
                            <label className="text-xs font-medium text-gray-500">{t('trainingCenter.participants.modal.trainingSession.sessionDate')}</label>
                            <p className="text-sm font-medium text-gray-900 mt-1">
                              {new Date(participantDetails.subscription.session.session_date).toLocaleDateString('fr-FR', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-600">{t('trainingCenter.participants.modal.error.loadDetails')}</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 p-4 flex justify-end">
              <button
                onClick={closeModal}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                {t('trainingCenter.participants.modal.close')}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ParticipantsPage;
